import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import type { Variant as TopicLinkVariant } from "scmp-app/components/content/content-topic-link";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { topStoriesItemContent$key } from "scmp-app/queries/__generated__/topStoriesItemContent.graphql";

import { Container, StyledContentItemHomePrimary, StyledContentItemHomeSecondary } from "./styles";

export type Props = {
  className?: string;
  currentTopicReference?: string;
  isReadingTimeEnabled?: boolean;
  isShowImage?: boolean;
  onClick?: (entityId?: string, urlAlias?: string, isSponsorArticle?: boolean) => void;
  reference: topStoriesItemContent$key;
  topicLinkVariant?: TopicLinkVariant;
  variant: "primary" | "secondary";
  withBookmark?: boolean;
  withCaption?: boolean;
  withCarousel?: boolean;
  withLiveTag?: boolean;
  withSection?: boolean;
  withSeriesShortLabel?: boolean;
  withTopic?: boolean;
};

export const TopStoriesItem: FunctionComponent<Props> = ({
  className,
  currentTopicReference,
  isReadingTimeEnabled = false,
  isShowImage = false,
  onClick,
  reference,
  topicLinkVariant,
  variant,
  withBookmark = false,
  withCaption = true,
  withCarousel = false,
  withLiveTag,
  withSection = true,
  withSeriesShortLabel = false,
  withTopic,
}) => {
  const content = useFragment(
    graphql`
      fragment topStoriesItemContent on Content {
        ...homePrimaryContentItemContent
        ...homeSecondaryContentItemContent
        ...helpersUseContentSponsorContent
      }
    `,
    reference,
  );

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);

  const sponsorArticleImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement: captureSponsorArticleImpressionEvent } =
    useTrackImpressionEventByIntersection({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: sponsorArticleImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const renderItem = () => {
    switch (variant) {
      case "primary":
        return (
          <StyledContentItemHomePrimary
            currentTopicReference={currentTopicReference}
            isReadingTimeEnabled={isReadingTimeEnabled}
            onClick={(entityId, urlAlias) => {
              onClick?.(entityId, urlAlias);
            }}
            reference={content}
            topicLinkVariant={topicLinkVariant}
            withBookmark={withBookmark}
            withCaption={withCaption}
            withCarousel={withCarousel}
            withComment={true}
            withLiveTag={withLiveTag ?? true}
            withSection={withSection}
            withSeriesShortLabel={withSeriesShortLabel}
            withTopic={withTopic}
          />
        );
      case "secondary":
        return (
          <StyledContentItemHomeSecondary
            currentTopicReference={currentTopicReference}
            isReadingTimeEnabled={isReadingTimeEnabled}
            onClick={(entityId, urlAlias) => {
              onClick?.(entityId, urlAlias, isSponsorArticle);
            }}
            ref={isSponsorArticle ? captureSponsorArticleImpressionEvent : null}
            reference={content}
            topicLinkVariant={topicLinkVariant}
            withBookmark={withBookmark}
            withComment={true}
            withImage={isShowImage}
            withLiveTag={withLiveTag ?? true}
            withSection={withSection}
            withSeriesShortLabel={withSeriesShortLabel}
            withSummary={true}
            withTopic={withTopic}
          />
        );
    }
  };

  return <Container className={className}>{renderItem()}</Container>;
};

TopStoriesItem.displayName = "TopStoriesItem";
